<script lang="ts">
  import { ChevronDown } from "lucide-svelte";
  import { onDestroy } from "svelte";
  import { createEventDispatcher } from "svelte";
  import { get } from "svelte/store";

  import { analytics } from "$lib/analytics";
  import Dropdown from "$lib/ui/system/Dropdown.svelte";
  import DropdownItem from "$lib/ui/system/DropdownItem.svelte";
  import DropdownItemGroup from "$lib/ui/system/DropdownItemGroup.svelte";
  import { user } from "$lib/user";

  export let live: boolean | undefined;
  export let start: number | undefined;
  export let end: number | undefined;
  export let duration: number | undefined;

  const dispatch = createEventDispatcher<{
    update: {
      live: boolean;
      start: number;
      end: number;
    };
  }>();

  $: formattedDateRange = formatDateRange(live, start, end, duration);
  $: formattedDuration = formatDuration(duration);
  let interval: number;
  $: if (live) {
    interval = window.setInterval(() => {
      formattedDateRange = formatDateRange(live, start, end, duration);
    }, 10000);
  } else {
    window.clearInterval(interval);
  }

  function formatDuration(duration: number | undefined): string {
    const units = [
      { unit: "w", seconds: 86400 * 7 },
      { unit: "d", seconds: 86400 },
      { unit: "h", seconds: 3600 },
      { unit: "m", seconds: 60 },
      { unit: "s", seconds: 1 },
    ];

    if (duration) {
      for (const { unit, seconds } of units) {
        const value = Math.floor(duration / seconds);
        if (value >= 1) {
          return `${value}${unit}`;
        }
      }
    }

    return "0s";
  }

  onDestroy(() => {
    window.clearInterval(interval);
  });

  interface RangeOption {
    name: string;
    secondsAgo: number;
  }

  const rangeOptions: Record<string, RangeOption> = {
    "1h": { name: "Past 1 hour", secondsAgo: 60 * 60 },
    "4h": { name: "Past 4 hours", secondsAgo: 4 * 60 * 60 },
    "1d": { name: "Past 1 day", secondsAgo: 24 * 60 * 60 },
    "2d": { name: "Past 2 days", secondsAgo: 2 * 24 * 60 * 60 },
    "1w": { name: "Past 1 week", secondsAgo: 7 * 24 * 60 * 60 },
  };

  function formatDateRange(
    live: boolean | undefined,
    start: number | undefined,
    end: number | undefined,
    duration: number | undefined,
  ) {
    const u = get(user);
    const timezone = (u?.logged_in && u?.timezone) || undefined;

    if (!start || !end || !duration) {
      return "";
    }

    const formatter = new Intl.DateTimeFormat("en-US", {
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "numeric",
      hour12: true,
      ...(timezone && { timeZone: timezone }),
    });

    if (live) {
      const now = new Date();
      const from = new Date(now.getTime() - duration * 1000);
      return `${formatter.format(from)} – now`;
    }

    const from = new Date(start * 1000);
    const to = new Date(end * 1000);
    return `${formatter.format(from)} – ${formatter.format(to)}`;
  }
</script>

{#if formattedDateRange}
  <Dropdown align="full">
    <button
      slot="button"
      class="btn btn-outlined py-1.5 gap-x-2 bg-white/5 min-w-[390px] justify-between tabular-nums"
    >
      <div class="flex items-center gap-x-3">
        <div class="bg-white/10 rounded px-2 flex items-center gap-1.5">
          {#if live}
            <div class="live-status"></div>
          {/if}
          {formattedDuration}
        </div>
        {formattedDateRange}
      </div>
      <ChevronDown size={20} class="text-zinc-500" />
    </button>

    <DropdownItemGroup>
      {#each Object.entries(rangeOptions) as [k, v]}
        <DropdownItem
          selected={formattedDuration === k}
          on:click={() => {
            analytics.track("Metrics Range Dropdown Selected", {
              range: k,
            });
            const now = new Date();
            const from = now.getTime() - v.secondsAgo * 1000;
            dispatch("update", {
              live: true,
              start: from / 1000,
              end: now.getTime() / 1000,
            });
          }}
        >
          <div class="flex gap-2">
            <div class="bg-white/10 rounded w-8 text-center">
              {k}
            </div>
            {v.name}
          </div>
        </DropdownItem>
      {/each}
    </DropdownItemGroup>

    <hr />

    <div class="m-3 text-xs text-white/60">
      To select a custom time period, click and drag along any chart.
    </div>
  </Dropdown>
{/if}

<style lang="postcss">
  .live-status {
    @apply w-[8px] h-[8px] bg-white/30 rounded-full bg-primary;
    filter: drop-shadow(0px 0px 8px #aaf398);
  }
</style>
